import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { makeTheme } from "../../themes";
import { themeToOverrides } from "../../themes/storeTheme";
import { auth } from "../../services/authService";
import { getStoreBySuffix } from "../../services/storeService";
import { IStoreTheme } from "../../classes/interfaces";

/**
 * Reprovê o tema da loja (por :suffix) na subárvore da jornada de compra.
 * Enquanto resolve — ou se falhar — usa o tema padrão MenuChef; as páginas
 * filhas têm seus próprios estados de carregamento, então não há tela quebrada.
 */
export default function StoreThemeProvider({
  children,
  suffix: suffixProp,
}: {
  children: React.ReactNode;
  /** Suffix explícito (ex.: telas /order/:saleId, que não têm :suffix na URL). */
  suffix?: string;
}) {
  const params = useParams<{ suffix: string }>();
  const suffix = suffixProp ?? params.suffix;
  const [theme, setTheme] = useState<IStoreTheme | null>(null);

  useEffect(() => {
    let active = true;
    if (!suffix) return;
    // auth() antes da busca: no primeiro acesso do navegador o sessionStorage
    // está vazio e api() sairia com "Bearer " em branco — a resposta vinha 401,
    // o catch caía no tema padrão e a loja só aparecia colorida no segundo
    // carregamento (quando a página da loja já havia gravado o token).
    auth()
      .then(() => getStoreBySuffix(suffix))
      .then((store) => { if (active) setTheme(store.theme ?? null); })
      .catch(() => { if (active) setTheme(null); });
    return () => { active = false; };
  }, [suffix]);

  const { styled, mui } = useMemo(() => makeTheme(themeToOverrides(theme)), [theme]);

  return (
    <ThemeProvider theme={styled}>
      <MuiThemeProvider theme={mui}>{children}</MuiThemeProvider>
    </ThemeProvider>
  );
}
